import {useTranslation} from "react-i18next";
import {useMothersOfWaste, useWasteById, useWasteFlavorById, WasteStatusCode,} from "../rpc/webrpc-client.gen.ts";
import {useWasteStatus, WasteStatus} from "./waste.tsx";
import {
    Box,
    Button,
    CardProps,
    DialogActions,
    DialogContent,
    DialogTitle,
    Modal,
    ModalDialog,
    Stack,
    Typography,
    useTheme,
} from "@mui/joy";
import {useNavigate} from "@tanstack/react-router";
import {NumericFormat} from "react-number-format";
import {SuspendedTile, Tile, TileProps} from "../components/Tile.tsx";
import {ActionButton} from "../components/ActionButton.tsx";
import {useLongPress} from "use-long-press";
import {useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTriangleExclamation} from "@fortawesome/pro-regular-svg-icons";
import {useRetireWasteMutation, useUnitArmap} from "../tq/tqx.tsx";

export interface WasteTileProps extends Omit<TileProps, "Subject"> {
    wasteId: string;
    actions?: TileProps["actions"];
}

// const StyledWasteTile
//     = styled(Card)<{ ownerState: WasteTileProps }>(({ownerState, theme}) => {
//     const paletteName = ownerState.color ?? "neutral";
//     const variant = ownerState.variant ?? "outlined";
//     return {
//         ...TILE_SX,
//         cursor: "pointer",
//         ...tileVariantSpecificSx({theme, color: paletteName, variant}),
//     };
// });

function wasteTileCosmetics({wasteStatus}: {
    wasteStatus?: WasteStatus | undefined,
}): {
    color: CardProps["color"], variant: CardProps["variant"],
    actionButtonColor: CardProps["color"], actionButtonVariant: CardProps["variant"],
} {
    if (wasteStatus?.attachment?.job && !wasteStatus.attachment.bundle) {
        return {
            color: "tertiary",
            variant: "outlined",
            actionButtonColor: "primary",
            actionButtonVariant: "solid",
        };
    }

    switch (wasteStatus?.code) {
        case WasteStatusCode.enum.STORAGE:
            return {
                color: "neutral",
                variant: "outlined",
                actionButtonColor: "primary",
                actionButtonVariant: "solid",
            };
        case WasteStatusCode.enum.INSPECTING:
            return {
                color: "warning",
                variant: "outlined",
                actionButtonColor: "primary",
                actionButtonVariant: "solid",
            };
        default:
            return {
                color: "primary",
                variant: "outlined",
                actionButtonColor: "primary",
                actionButtonVariant: "solid",
            };
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function WasteTile({wasteId, children, actions, color: inColor, variant: _, ...rest}: WasteTileProps) {
    const {t} = useTranslation();
    const wasteTqrv = useWasteById({wasteId});
    const waste = wasteTqrv.data;
    const flavorTqrv
        = useWasteFlavorById(waste ? {wasteFlavorId: waste.flavor.id} : undefined);
    const statusTqrv
        = useWasteStatus(waste ? {wasteId} : undefined);
    const wasteStatus = statusTqrv.data;
    const oyaTqrv
        = useMothersOfWaste(waste ? {wasteId} : undefined);
    const oyas = oyaTqrv.data;
    // TODO: integrate passed-in cosmetic stuff
    const cosmetics = wasteTileCosmetics({wasteStatus});
    const navigate = useNavigate();
    const [isRetireDialogOpen, setIsRetireDialogOpen]
        = useState<boolean>(false);
    const bindLongPress = useLongPress(() => {
        setIsRetireDialogOpen(true);
    })();
    const {mutateAsync: sendRetire} = useRetireWasteMutation();
    const theme = useTheme();

    const {data: units} = useUnitArmap();
    const flavor = flavorTqrv.data;
    const invUnitId = flavor?.invoiceUnitId;
    const maniUnitId = flavor?.manifestUnitId;
    const invUnit = invUnitId ? units?.byId.get(invUnitId) : undefined;
    const maniUnit = maniUnitId ? units?.byId.get(maniUnitId) : undefined;

    if (!waste || !flavorTqrv.isSuccess || !statusTqrv.isSuccess) {
        return <SuspendedTile/>;
    }

    const isManiLinked = waste.maniQ == waste.invQ &&
        waste.maniV == waste.invV && waste.maniW == waste.invW;
    const makeStatusLine = (code?: WasteStatusCode) => {
        switch (code) {
            case WasteStatusCode.enum.AWAITING_PICKUP:
                return <Box>{t("bundleStatus.awaitingPickup")}</Box>;
            case WasteStatusCode.enum.LOADED:
                return <Box>{t("bundleStatus.onloaded")}</Box>;
            case WasteStatusCode.enum.INSPECTING:
                return <Box>{t("wasteStatus.inspectionInProgress")}</Box>;
            case WasteStatusCode.enum.STORAGE:
                return <Box>{t("wasteStatus.stored")}</Box>;
            case WasteStatusCode.enum.SPLIT:
                return <Box>{t("wasteStatus.split")}</Box>
            default:
                return <Box>status unknown</Box>;
        }
    };
    const palette = theme.palette[cosmetics.color ?? "neutral"];

    const color = inColor ?? cosmetics.color;
    let oyaName: string | undefined;
    if (oyas) {
        if (oyas.length > 0) {
            oyaName = oyas[0]?.extras?.label ?? oyas[0]?.flavor.displayName;
            if (!!oyaName && oyas.length > 1) {
                oyaName = `${oyaName}${t("etc")}`;
            }
        }
    }

    const statusLine = makeStatusLine(wasteStatus?.code);
    return <Tile color={color}
                 Subject={(props) => {
                     return <Box {...props}>{flavor?.displayName}</Box>
                 }}
                 actions={actions}
                 {...rest}>
        <Box sx={{
            "&:hover": {
                backgroundColor: palette[`${cosmetics.variant ?? "soft"}HoverBg`],
            },
            alignItems: "center",
        }}>
            <Stack direction="column">
                <Stack direction="column"
                       sx={{alignItems: "center", cursor: "pointer"}}
                       onClick={() => {
                           void navigate({to: "/waste/$id/cargo", params: {id: waste.id}});
                       }}
                       {...bindLongPress}
                >
                    {oyaName
                        ? <Box sx={{fontSize: "sm"}}>{t("bornFrom", {oya: oyaName})}</Box>
                        : null}
                    <Stack direction="row">
                        <Box sx={{mr: 1}}>{t("invoiceAbbrev")}</Box>
                        {waste.invQ
                            ? <Box sx={{flex: 1}}>
                                <NumericFormat readOnly={true} displayType="text"
                                               value={waste.invQ / 10}
                                               decimalScale={1} suffix={invUnit?.displayName}/>
                            </Box>
                            : null}
                        {waste.invV
                            ? <Box sx={{flex: 1}}>
                                <NumericFormat readOnly={true} displayType="text"
                                               value={waste.invV / 10}
                                               decimalScale={1} suffix={t("volumeUnit")}/>
                            </Box>
                            : null}
                        {waste.invW
                            ? <Box sx={{flex: 1}}>
                                <NumericFormat readOnly={true} displayType="text"
                                               value={waste.invW / 1000}
                                               decimalScale={3} suffix={t("weightUnit")}/>
                            </Box>
                            : null}
                        <Box sx={{mx: 1}}>{t("maniAbbrev")}</Box>
                        {isManiLinked
                            ? t("linked")
                            : <>
                                {waste.maniQ
                                    ? <Box sx={{flex: 1}}>
                                        <NumericFormat readOnly={true} displayType="text"
                                                       value={waste.maniQ / 10}
                                                       decimalScale={1} suffix={maniUnit?.displayName}/>
                                    </Box>
                                    : null}
                                {waste.maniV
                                    ? <Box sx={{flex: 1}}>
                                        <NumericFormat readOnly={true} displayType="text"
                                                       value={waste.maniV / 10}
                                                       decimalScale={1} suffix={t("volumeUnit")}/>
                                    </Box>
                                    : null}
                                {waste.maniW
                                    ? <Box sx={{flex: 1}}>
                                        <NumericFormat readOnly={true} displayType="text"
                                                       value={waste.maniW / 1000}
                                                       decimalScale={3} suffix={t("weightUnit")}/>
                                    </Box>
                                    : null}
                            </>}
                    </Stack>
                    {statusLine}
                </Stack>
                {children}
            </Stack>
            <Modal open={isRetireDialogOpen} sx={{
                display: "flex",
                alignItems: "center", justifyContent: "center"
            }}>
                <ModalDialog>
                    <DialogTitle>
                        <FontAwesomeIcon icon={faTriangleExclamation}/>
                        <Typography level="title-lg">{t("confirm")}</Typography>
                    </DialogTitle>
                    <DialogContent>
                        {t("reallyRetireWastePrompt")}
                    </DialogContent>
                    <DialogActions>
                        <ActionButton variant="solid" color="danger" perform={() => {
                            return sendRetire({wasteId: waste.id})
                                .then(() => {
                                    setIsRetireDialogOpen(false);
                                })
                        }}>
                            {t("doRetire")}
                        </ActionButton>
                        <Button variant="soft" color="neutral"
                                onClick={() => {
                                    setIsRetireDialogOpen(false);
                                }}>
                            {t("cancel")}
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    </Tile>;
}

